<template>
  <div class="auth create-page" :class="{ phone }">
    <!-- On a phone (SPEC §4.11): the settings view, then the opponent view. -->
    <template v-if="phone">
      <template v-if="step === 'settings'">
        <NavBar />
        <h3>{{ t('create-title') }}</h3>
        <div class="form">
          <label>{{ t('create-board-size') }}
            <select v-model.number="grid" name="grid">
              <option v-for="n in sizes" :key="n" :value="n">{{ n }}</option>
            </select>
          </label>
          <label>{{ t('create-handicap') }}
            <select v-model.number="handicap" name="handicap">
              <option v-for="h in handicaps" :key="h.value" :value="h.value">{{ h.text }}</option>
            </select>
          </label>
          <label>{{ t('create-colour') }}
            <select v-model="colour" name="colour">
              <option value="black">{{ t('create-colour-black') }}</option>
              <option value="white">{{ t('create-colour-white') }}</option>
              <option v-if="!handicap" value="nigiri">{{ t('create-colour-nigiri') }}</option>
            </select>
          </label>
          <p v-if="handicapNote" class="note">{{ handicapNote }}</p>
          <div class="choices">
            <button type="button" class="choose" @click="chooseOpponent">{{ t('button-choose-opponent') }}</button>
            <ActionButton :key="'invite:' + actionKey" class="invite" :disabled="busy" :action="invite" :label="t(inviteLabel)" />
          </div>
          <p v-if="message" class="warn">{{ message }}</p>
        </div>
      </template>
      <template v-else>
        <div class="topbar">
          <button type="button" class="back" @click="backToSettings">&lt; {{ t('nav-back-step') }}</button>
        </div>
        <p class="summary">{{ summary }}</p>
        <div class="form opponents-view">
          <input v-model="filter" type="search" name="opponentFilter" :placeholder="t('create-opponent-filter')" />
          <ul class="opponents" role="listbox" :aria-label="t('create-opponent')">
            <li v-if="aiOffered" role="option" :aria-selected="opponent === 'ai'" class="ai-entry"
                :class="{ chosen: opponent === 'ai' }" @click="opponent = 'ai'">
              <span>{{ t('create-opponent-ai') }}</span>
              <div v-if="opponent === 'ai'" class="ai-level" @click.stop>
                <span>{{ t('create-ai-level') }}</span>
                <select v-model="aiLevel" name="aiLevel" :aria-label="t('create-ai-level')">
                  <option v-for="l in aiLevels" :key="l" :value="l">{{ aiLevelText(l) }}</option>
                </select>
              </div>
            </li>
            <li role="option" :aria-selected="opponent === 'self'" :class="{ chosen: opponent === 'self' }"
                @click="opponent = 'self'">{{ t('create-opponent-self') }}</li>
            <li v-for="u in shownUsers" :key="u.id" role="option" :aria-selected="opponent === u.id"
                :class="{ chosen: opponent === u.id }" @click="opponent = u.id">{{ u.displayName }}</li>
          </ul>
          <p v-if="message" class="warn">{{ message }}</p>
        </div>
        <div class="bottom">
          <ActionButton :key="'start:' + actionKey" class="start" :disabled="busy || opponent === null" :action="create" :label="t('button-create')" />
        </div>
      </template>
    </template>

    <template v-else>
      <NavBar />
      <h3>{{ t('create-title') }}</h3>

      <form novalidate @submit.prevent="create">
        <label>{{ t('create-board-size') }}
          <select v-model.number="grid" name="grid">
            <option v-for="n in sizes" :key="n" :value="n">{{ n }}</option>
          </select>
        </label>
        <label>{{ t('create-handicap') }}
          <select v-model.number="handicap" name="handicap">
            <option v-for="h in handicaps" :key="h.value" :value="h.value">{{ h.text }}</option>
          </select>
        </label>
        <label>{{ t('create-colour') }}
          <select v-model="colour" name="colour">
            <option value="black">{{ t('create-colour-black') }}</option>
            <option value="white">{{ t('create-colour-white') }}</option>
            <option v-if="!handicap" value="nigiri">{{ t('create-colour-nigiri') }}</option>
          </select>
        </label>
        <p v-if="handicapNote" class="note">{{ handicapNote }}</p>
        <label>{{ t('create-opponent') }}
          <input v-model="filter" type="search" name="opponentFilter" :placeholder="t('create-opponent-filter')" />
          <select v-model="opponent" name="opponent" size="6">
            <option value="invite" class="invite">{{ t(inviteLabel) }}</option>
            <option v-if="aiOffered" value="ai" class="ai-entry">{{ t('create-opponent-ai') }}</option>
            <option value="self">{{ t('create-opponent-self') }}</option>
            <option v-for="u in shownUsers" :key="u.id" :value="u.id">{{ u.displayName }}</option>
          </select>
        </label>
        <label v-if="opponent === 'ai'">{{ t('create-ai-level') }}
          <select v-model="aiLevel" name="aiLevel">
            <option v-for="l in aiLevels" :key="l" :value="l">{{ aiLevelText(l) }}</option>
          </select>
        </label>
        <p v-if="message" class="warn">{{ message }}</p>
        <ActionButton :key="'create:' + actionKey" :disabled="busy" :action="create" :label="t('button-create')" />
      </form>
    </template>
  </div>
</template>

<script>
import NavBar from './NavBar';
import ActionButton from './ActionButton';
import api from '@/api/api';
import extensions from '@/extensions';
import { t, errorText } from '@/texts';
import { handicapText, ziText, aiLevels, aiLevelText } from '@/text';

// Creates a standard game: board size, handicap, the creator's colour (or nigiri, only without a
// handicap) and the opponent, who may be the creator (a game played alone). The request is a game in the shape it is read back. The
// first entry instead makes an invitation (SPEC §2.5) with the same settings, the creator alone in
// it: black the creator, or absent for white (the friend plays black) and for nigiri.
// For accounts the admin allowed, the AI is an opponent too, at a level (plan
// 2026-10-28-ai-games.md §4.1), remembered in this browser.
// Screens narrower than this get the two views (SPEC §4.11).
const phoneQuery = window.matchMedia('(max-width: 599px)');
const levelKey = 'slowgo.aiLevel';

function rememberedLevel() {
  try {
    const l = window.localStorage.getItem(levelKey);
    if (aiLevels.includes(l)) return l;
  } catch (e) {
    // no storage: the default
  }
  return '5k';
}

export default {
  name: 'CreateGamePage',
  components: { ActionButton, NavBar },
  data() {
    return {
      sizes: [9, 13, 19],
      grid: 19,
      // 0 even, 1 让先, 2-9 stones (SPEC §2.3).
      handicap: 0,
      colour: 'nigiri',
      users: [],
      filter: '',
      opponent: null,
      message: null,
      busy: false,
      phone: phoneQuery.matches,
      pushedStep: false,
      aiLevels,
      aiLevel: rememberedLevel(),
    };
  },
  computed: {
    actionKey() { return JSON.stringify([this.grid, this.handicap, this.colour, this.opponent, this.aiLevel, this.step]); },
    handicaps() {
      return [{ value: 0, text: t('create-handicap-none') }, ...[1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => ({ value: n, text: handicapText(n) }))];
    },
    // What the game will be, from the board's side: who places, who moves first, komi and return.
    handicapNote() {
      if (this.handicap === 1) return t('create-handicap-note-first');
      if (this.handicap > 1) return t('create-handicap-note', { n: this.handicap, half: ziText(this.handicap / 2) });
      return null;
    },
    me() { return this.$store.state.user; },
    aiPlayer() { return this.$store.state.site.aiPlayer || 0; },
    aiOffered() { return Boolean(this.me && this.me.aiGames && this.aiPlayer); },
    inviteLabel() { return extensions.createOpponentInviteLabel || 'create-opponent-invite'; },
    // The phone's view: the settings, or the opponent list (?step=opponent, so Back returns).
    step() { return this.$route.query.step === 'opponent' ? 'opponent' : 'settings'; },
    summary() {
      const colour = { black: 'invitation-colour-black', white: 'invitation-colour-white', nigiri: 'create-colour-nigiri' }[this.colour];
      if (this.handicap) return t('create-summary-handicap', { grid: this.grid, handicap: handicapText(this.handicap), colour: t(colour) });
      return t('create-summary', { grid: this.grid, colour: t(colour) });
    },
    // Other accounts by display name, those matching the filter.
    shownUsers() {
      const f = this.filter.trim().toLowerCase();
      return this.users
        .filter(u => u.id !== this.me.id && u.displayName.toLowerCase().includes(f))
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
    },
  },
  watch: {
    // Nigiri goes with a handicap: black, as most often the weaker player asks for the game.
    handicap(h) {
      if (h && this.colour === 'nigiri') this.colour = 'black';
    },
    aiLevel(l) {
      try {
        window.localStorage.setItem(levelKey, l);
      } catch (e) {
        // not remembered
      }
    },
  },
  methods: {
    t,
    aiLevelText,
    chooseOpponent() {
      this.message = null;
      this.pushedStep = true;
      this.$router.push({ query: { step: 'opponent' } });
    },
    // As the phone's Back button, unless the page was opened at the opponent view.
    backToSettings() {
      this.message = null;
      if (this.pushedStep) this.$router.back();
      else this.$router.replace({ query: {} });
    },
    onPhoneChange(e) {
      this.phone = e.matches;
    },
    // The phone's invite button.
    async invite() {
      const chosen = this.opponent;
      this.opponent = 'invite';
      await this.create();
      this.opponent = chosen;
    },
    request() {
      const me = this.me.id;
      const alone = this.opponent === 'self';
      const ai = this.opponent === 'ai';
      const other = alone ? me : ai ? this.aiPlayer : this.opponent;
      const setup = { type: 'standard', grid: this.grid };
      if (this.handicap) setup.handicap = this.handicap;
      if (ai) setup.aiLevel = this.aiLevel;
      if (this.colour === 'nigiri' && !alone) setup.nigiri = true;
      else setup.blackPlayer = this.colour === 'white' ? other : me;
      const players = alone ? [{ id: me }] : [{ id: me }, { id: other }];
      return { setup, players };
    },
    invitationRequest() {
      const setup = { type: 'standard', grid: this.grid };
      if (this.handicap) setup.handicap = this.handicap;
      if (this.colour === 'nigiri') setup.nigiri = true;
      else if (this.colour === 'black') setup.blackPlayer = this.me.id;
      return { game: { setup, players: [{ id: this.me.id }] } };
    },
    async create() {
      this.message = null;
      if (this.opponent === null) {
        this.message = t('error-choose-opponent');
        return;
      }
      this.busy = true;
      try {
        if (this.opponent === 'invite') {
          const { id } = await api.call('POST', 'invitations', this.invitationRequest());
          this.$router.push({ name: 'invitation', params: { id }, query: { send: '1' } });
          return;
        }
        const { id } = await api.call('POST', api.GAMES, this.request());
        // ?send=1: a module may offer sending the new game on arrival (SPEC §4.11).
        const toPerson = this.opponent !== 'self' && this.opponent !== 'ai';
        this.$router.push({ name: 'game', params: { id }, query: toPerson ? { send: '1' } : {} });
      } catch (e) {
        const code = e.msg || String(e);
        this.message = code === 'forbidden' && this.opponent === 'ai' ? t('error-ai-not-allowed') : errorText(code);
      } finally {
        this.busy = false;
      }
    },
  },
  async created() {
    phoneQuery.addListener(this.onPhoneChange);
    this.users = await api.call('GET', 'users');
  },
  beforeDestroy() {
    phoneQuery.removeListener(this.onPhoneChange);
  },
};
</script>

<style src="./auth.css"></style>
<style scoped>
select {
  width: 100%;
  font-size: 16px;
}

.note {
  color: #888;
  font-size: 14px;
  line-height: 1.4;
  margin: 2px 0 10px;
  text-align: left;
}

option.invite {
  font-weight: bold;
  color: #8b5e34;
  border-bottom: 1px solid #ccc;
}

/* The AI, apart from the accounts: bold, a wood line under it. */
option.ai-entry {
  font-weight: bold;
  border-bottom: 1px solid #c8a97e;
}

.opponents li.ai-entry {
  font-weight: bold;
  border-bottom: 2px solid #c8a97e;
}

.opponents .ai-level {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  font-size: 15px;
  font-weight: normal;
  color: #555;
}

.opponents .ai-level select {
  width: auto;
  flex: none;
}

/* The phone's views. */
.phone .form {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 10px auto;
  max-width: 360px;
  padding: 0 12px;
}

.phone .choices {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
  margin-top: 12px;
}

.phone .choices button, .phone .choices .wechat-action {
  flex: 1 1 140px;
  max-width: 170px;
  height: 48px;
  margin: 4px;
  font-size: 16px;
}

/* The two ways on (SPEC §4.1 buttons): inviting a friend is the main one, wood brown; choosing a
   registered player is secondary, white with the wood outline. */
.phone .choices button.choose {
  color: #2c3e50;
  background: #fff;
  border: 1px solid #c9b48a;
  border-radius: 10px;
}

.phone .choices button.invite, .phone .choices .wechat-action.invite {
  color: #fff;
  background: #8b5e34;
  border: 1px solid #8b5e34;
  border-radius: 10px;
}

.phone .topbar {
  text-align: left;
  padding: 6px 4px;
  border-bottom: 1px solid #ddd;
}

.phone .topbar .back {
  width: auto;
  height: 36px;
  margin: 0;
  border: none;
  background: none;
  font-size: 16px;
  color: #8b5e34;
}

.phone .summary {
  margin: 10px 0 4px;
  font-size: 18px;
}

.phone .opponents-view {
  padding-bottom: 80px;
}

.opponents {
  list-style: none;
  width: 100%;
  margin: 8px 0;
  padding: 0;
  text-align: left;
}

.opponents li {
  padding: 12px 10px;
  border-bottom: 1px solid #eee;
  font-size: 16px;
  overflow-wrap: anywhere;
  cursor: pointer;
}

.opponents li.chosen {
  background: #ffd39a;
  font-weight: bold;
}

.phone .bottom {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 10px 12px;
  background: #fff;
  border-top: 1px solid #ddd;
}

.phone .bottom .start {
  width: 100%;
  max-width: 360px;
  height: 48px;
  margin: 0;
  font-size: 18px;
}
</style>
